/**
 * 모빌리티(오토바이·스쿠터) 배기량 클래스 — 단일 출처
 * (설계: docs/superpowers/specs/2026-09-16-mobility-station-hub-design.md §2-2)
 *
 * 클래스는 `products.tags` 의 `bike-class:<key>` 태그 **1개**로만 결정된다.
 * 태그가 없거나 알 수 없는 값이면 `"unknown"` — 칩에 나오지 않고 "전체"에서만 보인다.
 *
 * 라벨·면허 문구는 i18n 메시지가 아니라 **이 파일의 상수**다(설계 §2-2·§4):
 * 클래스 축은 파트너 데이터(태그)에 묶여 있어 번역 키로 흩어 놓으면 태그가
 * 늘어날 때마다 5개 언어 파일을 동시에 고쳐야 하기 때문이다.
 */

import type { Locale } from "@/i18n/routing";

export type BikeClassKey =
  | "moped"
  | "moped2"
  | "c250"
  | "c400"
  | "large"
  | "premium"
  | "trike";

/** 클래스를 못 정한 상품 — 칩 필터에서 제외되고 "전체"에만 포함된다. */
export type BikeClassKeyOrUnknown = BikeClassKey | "unknown";

export interface BikeClass {
  key: BikeClassKey;
  /** `products.tags` 에 들어가는 원문 태그 */
  tag: `bike-class:${BikeClassKey}`;
  /** 칩 라벨(짧게) */
  label: Record<Locale, string>;
  /** 섹션 헤더(조금 더 길게) */
  head: Record<Locale, string>;
  /**
   * 면허 안내 한 줄 — 일본 면허 등급만(10-15, ja 와 같은 규칙). 국제운전면허 등급(AM/A1/A)은
   * 붙이지 않는다: 상품 상세·메타 설명·모델 허브·허브 시트가 모두 이 문구를 쓴다.
   */
  license: Record<Locale, string>;
}

/**
 * 표시 순서 = 배기량 오름차순 → Premium → 트라이크 (설계 §2-2 "순서도 이 순서").
 * 칩·카탈로그 섹션·모델 정렬이 전부 이 배열 순서를 따른다.
 */
export const BIKE_CLASSES: readonly BikeClass[] = [
  {
    key: "moped",
    tag: "bike-class:moped",
    label: {
      ko: "~50cc",
      en: "≤50cc",
      ja: "〜50cc",
      "zh-CN": "~50cc",
      "zh-TW": "~50cc",
    },
    head: {
      ko: "~50cc",
      en: "≤50cc",
      ja: "原付一種（〜50cc）",
      "zh-CN": "~50cc",
      "zh-TW": "~50cc",
    },
    license: {
      ko: "원동기 면허 이상 (일본 보통 자동차 면허로도 가능)",
      en: "Moped licence or above (a Japanese car licence also works)",
      ja: "原付免許以上（普通自動車免許でも可）",
      "zh-CN": "原付驾照以上（普通汽车驾照亦可）",
      "zh-TW": "原付駕照以上（普通汽車駕照亦可）",
    },
  },
  {
    key: "moped2",
    tag: "bike-class:moped2",
    label: {
      ko: "~125cc",
      en: "≤125cc",
      ja: "〜125cc",
      "zh-CN": "~125cc",
      "zh-TW": "~125cc",
    },
    head: {
      ko: "51~125cc",
      en: "51–125cc",
      ja: "原付二種（〜125cc）",
      "zh-CN": "51~125cc",
      "zh-TW": "51~125cc",
    },
    license: {
      ko: "소형 이륜 면허 이상",
      en: "Small motorcycle licence or above",
      ja: "小型限定普通二輪免許以上",
      "zh-CN": "小型限定普通二轮驾照以上",
      "zh-TW": "小型限定普通二輪駕照以上",
    },
  },
  {
    key: "c250",
    tag: "bike-class:c250",
    label: {
      ko: "~250cc",
      en: "≤250cc",
      ja: "〜250cc",
      "zh-CN": "~250cc",
      "zh-TW": "~250cc",
    },
    head: {
      ko: "보통 2륜 (~250cc)",
      en: "Standard motorcycle (≤250cc)",
      ja: "普通二輪（〜250cc）",
      "zh-CN": "普通二轮（~250cc）",
      "zh-TW": "普通二輪（~250cc）",
    },
    license: {
      ko: "보통 이륜 면허 이상",
      en: "Standard motorcycle licence or above",
      ja: "普通二輪免許以上",
      "zh-CN": "普通二轮驾照以上",
      "zh-TW": "普通二輪駕照以上",
    },
  },
  {
    key: "c400",
    tag: "bike-class:c400",
    label: {
      ko: "~400cc",
      en: "≤400cc",
      ja: "〜400cc",
      "zh-CN": "~400cc",
      "zh-TW": "~400cc",
    },
    head: {
      ko: "보통 2륜 (~400cc)",
      en: "Standard motorcycle (≤400cc)",
      ja: "普通二輪（〜400cc）",
      "zh-CN": "普通二轮（~400cc）",
      "zh-TW": "普通二輪（~400cc）",
    },
    license: {
      ko: "보통 이륜 면허 이상",
      en: "Standard motorcycle licence or above",
      ja: "普通二輪免許以上",
      "zh-CN": "普通二轮驾照以上",
      "zh-TW": "普通二輪駕照以上",
    },
  },
  {
    key: "large",
    tag: "bike-class:large",
    label: {
      ko: "401cc~",
      en: "401cc+",
      ja: "401cc〜",
      "zh-CN": "大型 401cc+",
      "zh-TW": "大型 401cc+",
    },
    head: {
      ko: "대형 2륜 (401cc 이상)",
      en: "Large motorcycle (401cc+)",
      ja: "大型二輪（401cc〜）",
      "zh-CN": "大型二轮（401cc以上）",
      "zh-TW": "大型二輪（401cc以上）",
    },
    license: {
      ko: "대형 이륜 면허",
      en: "Large motorcycle licence",
      ja: "大型二輪免許",
      "zh-CN": "大型二轮驾照",
      "zh-TW": "大型二輪駕照",
    },
  },
  {
    key: "premium",
    tag: "bike-class:premium",
    label: {
      ko: "Premium",
      en: "Premium",
      ja: "Premium",
      "zh-CN": "Premium",
      "zh-TW": "Premium",
    },
    head: {
      ko: "프리미엄",
      en: "Premium",
      ja: "プレミアム",
      "zh-CN": "高端车型",
      "zh-TW": "頂級車款",
    },
    license: {
      ko: "대형 이륜 면허",
      en: "Large motorcycle licence",
      ja: "大型二輪免許",
      "zh-CN": "大型二轮驾照",
      "zh-TW": "大型二輪駕照",
    },
  },
  {
    key: "trike",
    tag: "bike-class:trike",
    label: {
      ko: "트라이크",
      en: "Trike",
      ja: "トライク",
      "zh-CN": "三轮车",
      "zh-TW": "三輪車",
    },
    head: {
      ko: "트라이크",
      en: "Trike",
      ja: "トライク",
      "zh-CN": "三轮车",
      "zh-TW": "三輪車",
    },
    license: {
      ko: "보통 자동차 면허",
      en: "Car licence",
      ja: "普通自動車免許",
      "zh-CN": "普通汽车驾照",
      "zh-TW": "普通汽車駕照",
    },
  },
] as const;

const BIKE_CLASS_BY_KEY = new Map<string, BikeClass>(
  BIKE_CLASSES.map((c) => [c.key, c]),
);

/** 정렬용 인덱스 — `BIKE_CLASSES` 순서, unknown 은 항상 맨 뒤. */
export function bikeClassOrder(key: BikeClassKeyOrUnknown): number {
  const index = BIKE_CLASSES.findIndex((c) => c.key === key);
  return index === -1 ? BIKE_CLASSES.length : index;
}

export function getBikeClass(key: BikeClassKeyOrUnknown): BikeClass | undefined {
  return BIKE_CLASS_BY_KEY.get(key);
}

/**
 * 필요 면허 문구 — 모델 허브·상품 상세 차량 정보·메타 설명·허브 시트가 **같은 함수**를 쓴다
 * (10-15: 허브 "보통 이륜 면허 이상 · 국제운전면허 A" vs 상세 "일본 보통이륜 면허 이상").
 * 일본 면허 등급만 — 국제운전면허 등급은 붙이지 않는다(ja 와 같은 규칙). 모르는 클래스면 null.
 */
export function bikeLicenseText(key: BikeClassKeyOrUnknown, locale: string): string | null {
  const license = getBikeClass(key)?.license;
  if (!license) return null;
  return license[locale as Locale] ?? license.en;
}

const TAG_PREFIX = "bike-class:";

/** `tags` 배열만 읽는 최소 입력 — Product 전체를 넘겨도 되고 태그만 넘겨도 된다. */
export interface HasTags {
  tags?: string[] | null;
}

/**
 * 상품의 배기량 클래스. `bike-class:` 태그가 여러 개면 **BIKE_CLASSES 순서상
 * 가장 앞선 것**을 쓴다(데이터 위반이지만 화면이 상품마다 흔들리지 않게 결정적).
 */
export function bikeClassOf(product: HasTags): BikeClassKeyOrUnknown {
  const tags = product?.tags;
  if (!tags || tags.length === 0) return "unknown";

  let best: BikeClassKey | null = null;
  let bestOrder = Number.POSITIVE_INFINITY;
  for (const raw of tags) {
    if (typeof raw !== "string") continue;
    const trimmed = raw.trim();
    if (!trimmed.startsWith(TAG_PREFIX)) continue;
    const key = trimmed.slice(TAG_PREFIX.length);
    const klass = BIKE_CLASS_BY_KEY.get(key);
    if (!klass) continue;
    const order = bikeClassOrder(klass.key);
    if (order < bestOrder) {
      best = klass.key;
      bestOrder = order;
    }
  }
  return best ?? "unknown";
}

/**
 * 요금제 접미사(인기/스탠다드 지점) 제거 정규식 — 설계 §2-1의 문자열 그대로.
 * 같은 차종이 "인기 지점"·"스탠다드 지점" 2개 상품으로 쪼개져 있어서, 카탈로그는
 * 이 접미사를 떼어낸 이름을 **모델 키**로 삼아 둘을 한 행으로 묶는다.
 */
export const MODEL_TIER_SUFFIX_RE =
  /（人気拠点）|（スタンダード拠点）|\s*\((인기|스탠다드) 지점\)|\s*\((Popular|Standard) stations\)|（(人气|标准|人氣|標準)据点）|（(人氣|標準)據點）/g;

/** 이름만 있는 최소 입력 — Product 를 그대로 넘겨도 된다. */
export interface HasName {
  name?: string | null;
}

/**
 * 카탈로그 모델 키 = 상품명에서 요금제 접미사를 뗀 뒤 trim.
 * 접미사가 없으면 상품명 그대로(= 모델 1개 = 상품 1개).
 */
export function modelKeyOf(product: HasName): string {
  const name = product?.name ?? "";
  return name.replace(MODEL_TIER_SUFFIX_RE, "").trim();
}
